import { RESET_ALL as RESET_ON_LOGOUT } from 'redux-store/reducers/common-reducer';
import { hideLoading, showLoading } from 'redux-store/reducers/loading-reducer';
import { addUserAction } from 'modules/tracking/providers/user-action-provider';

import { createReducer } from 'utils/redux';
import { API, get, post } from 'utils/api';
import { sortParticipants } from 'utils/campaignParticipants';
import { logError } from 'utils/log';

import type { Action, Campaign, CampaignDuplicationForm, Subcampaign, ThunkAction } from 'types';

const LOADING = 'campaignDuplication/LOADING';
const LOADED = 'campaignDuplication/LOADED';
const RESET = 'campaignDuplication/RESET';

const { CAMPAIGNS } = API.END_POINTS;

const initialState = {
  isLoading: false,
  subcampaigns: []
};

function setLoadingStatus(status: boolean): Action {
  return {
    type: LOADING,
    payload: {
      status
    }
  };
}

export function resetCampaignDuplication() {
  return {
    type: RESET,
    payload: {}
  };
}

function receiveSubcampaigns(subcampaigns: Array<Subcampaign>): Action {
  return {
    type: LOADED,
    payload: {
      subcampaigns
    }
  };
}

export function fetchSubcampaigns(campaignId: number): ThunkAction {
  const url = `${CAMPAIGNS.getCampaignSubcampaigns(campaignId)}?states=accepted`;

  return (dispatch) => {
    dispatch(setLoadingStatus(true));

    return get(url)
      .then((response) => {
        dispatch(receiveSubcampaigns(sortParticipants(response.data)));
        dispatch(setLoadingStatus(false));
      })
      .catch((error) => {
        dispatch(setLoadingStatus(false));

        throw error;
      });
  };
}

export function duplicateCampaign(campaign: Campaign, data: CampaignDuplicationForm): ThunkAction {
  const url = CAMPAIGNS.duplicateCampaign(campaign.brandProfileId, campaign.id);
  let newCampaign;

  return async (dispatch) => {
    dispatch(showLoading(url));

    try {
      const response = await post(url, { data }, { suppressGlobalNotification: true });

      newCampaign = response.data;

      addUserAction({
        eventCategory: 'campaigns',
        eventAction: 'campaign-duplicated'
      });

      await dispatch(resetCampaignDuplication());
      await dispatch(hideLoading(url));
    } catch (e) {
      logError(e);
      await dispatch(hideLoading(url));
      throw e;
    }

    return Promise.resolve(newCampaign);
  };
}

export default createReducer<any, any>('campaignDuplication', initialState, {
  [RESET_ON_LOGOUT]: () => initialState,
  [RESET]: () => initialState,
  [LOADING]: (state, { status }) => ({
    ...state,
    isLoading: status
  }),
  [LOADED]: (state, { subcampaigns }) => ({
    ...state,
    subcampaigns
  })
});
